import { useEffect, useState } from "react";
import Button from "@/components/base/Button";
import { LOGO_URL, MAPS_LINK, NAV_ITEMS } from "@/pages/home/constants";

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeId, setActiveId] = useState("#inicio");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const sections = NAV_ITEMS.map((item) => document.querySelector(item.href)).filter(
      (el): el is Element => Boolean(el),
    );

    if (sections.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);

        if (visible[0]) {
          setActiveId(`#${visible[0].target.id}`);
        }
      },
      { rootMargin: "-45% 0px -50% 0px", threshold: [0, 0.25, 0.5, 1] },
    );

    sections.forEach((section) => observer.observe(section));
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!menuOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setMenuOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [menuOpen]);

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-200 ${
        scrolled || menuOpen
          ? "bg-background-50/95 border-b border-background-200"
          : "bg-background-50/80 border-b border-transparent"
      }`}
    >
      <div className="mx-auto flex h-16 w-full items-center justify-between px-4 md:h-20 md:px-6">
        <a
          href="#inicio"
          aria-label="Iokus Guatapé — inicio"
          className="flex flex-none items-center"
        >
          <img
            src={LOGO_URL}
            alt="Logo de Iokus, juegos y juguetes de madera hechos en Colombia"
            title="Iokus Guatapé juegos y juguetes de madera"
            className="h-7 w-auto md:h-9"
          />
        </a>

        <nav
          aria-label="Navegación principal"
          className="hidden flex-1 items-center justify-center gap-1 md:flex"
        >
          {NAV_ITEMS.map((item) => {
            const isActive = activeId === item.href;
            return (
              <a
                key={item.href}
                href={item.href}
                aria-current={isActive ? "true" : undefined}
                className={`relative rounded-md px-3 py-2 text-sm font-label font-semibold whitespace-nowrap transition-colors duration-200 lg:px-4 ${
                  isActive
                    ? "text-primary-700"
                    : "text-foreground-600 hover:text-foreground-950"
                }`}
              >
                {item.label}
                <span
                  className={`pointer-events-none absolute inset-x-3 -bottom-0.5 h-0.5 rounded-full bg-primary-500 transition-opacity duration-200 lg:inset-x-4 ${
                    isActive ? "opacity-100" : "opacity-0"
                  }`}
                  aria-hidden="true"
                ></span>
              </a>
            );
          })}
        </nav>

        <div className="flex flex-none items-center gap-2">
          <Button
            href={MAPS_LINK}
            variant="primary"
            external
            fbEvent="FindLocation"
            fbLabel="header"
            className="hidden px-4 md:inline-flex md:px-6"
          >
            <i className="ri-map-pin-2-line text-base" aria-hidden="true"></i>
            Cómo llegar
          </Button>

          <button
            type="button"
            onClick={() => setMenuOpen((open) => !open)}
            aria-label={menuOpen ? "Cerrar menú" : "Abrir menú"}
            aria-expanded={menuOpen}
            className="flex h-10 w-10 items-center justify-center rounded-md border border-background-300 text-foreground-900 cursor-pointer transition-colors duration-200 hover:bg-background-100 md:hidden"
          >
            <i className={menuOpen ? "ri-close-line text-xl" : "ri-menu-line text-xl"} aria-hidden="true"></i>
          </button>
        </div>
      </div>

      <div
        className={`overflow-hidden border-t border-background-200 bg-background-50 transition-[max-height] duration-300 ease-out md:hidden ${
          menuOpen ? "max-h-[520px]" : "max-h-0 border-t-transparent"
        }`}
      >
        <nav aria-label="Navegación móvil" className="mx-auto flex w-full flex-col px-4 py-3">
          {NAV_ITEMS.map((item) => {
            const isActive = activeId === item.href;
            return (
              <a
                key={item.href}
                href={item.href}
                onClick={() => setMenuOpen(false)}
                className={`rounded-md px-3 py-3 text-sm font-label font-semibold transition-colors duration-200 ${
                  isActive
                    ? "bg-primary-100 text-primary-800"
                    : "text-foreground-700 hover:bg-background-100"
                }`}
              >
                {item.label}
              </a>
            );
          })}
          <Button
            href={MAPS_LINK}
            variant="primary"
            external
            fbEvent="FindLocation"
            fbLabel="header"
            className="mt-3 w-full"
          >
            <i className="ri-map-pin-2-line text-base" aria-hidden="true"></i>
            Cómo llegar
          </Button>
        </nav>
      </div>
    </header>
  );
}